import { useEffect, useMemo, useState } from 'react';
import type { StockAnalogSnapshot } from '@/mocks/stockAnalogMock';
import type { ReliableAnalogPick } from '@/services/api';
import { stockService } from '@/services/api';
import {
  computeSelfAnalogStrength,
  sortReliablePicksByStrength,
  type SelfAnalogStrength,
} from '@/utils/reliablePickStrength';

export function useReliablePicksRanked(
  rows: ReliableAnalogPick[],
  referenceDate?: string,
) {
  const [strengthBySymbol, setStrengthBySymbol] = useState<
    Record<string, SelfAnalogStrength>
  >({});
  const [snapshotsBySymbol, setSnapshotsBySymbol] = useState<
    Record<string, StockAnalogSnapshot>
  >({});
  const [loading, setLoading] = useState(true);

  const symbolsKey = useMemo(
    () => rows.map((r) => r.symbol).join(','),
    [rows],
  );

  useEffect(() => {
    if (!rows.length) {
      setStrengthBySymbol({});
      setSnapshotsBySymbol({});
      setLoading(false);
      return;
    }

    const symbols = symbolsKey.split(',').filter(Boolean);
    let cancelled = false;
    setLoading(true);

    (async () => {
      const results = await Promise.all(
        symbols.map(async (symbol) => {
          const snapshot = await stockService.getMechanicalAnalogs(symbol, {
            date: referenceDate,
            topK: 20,
          });
          if (!snapshot) return { symbol, snapshot: null, strength: null };
          const lens = snapshot.lenses.find((l) => l.id === 'mechanical');
          const matches = lens?.matches ?? [];
          return {
            symbol,
            snapshot,
            strength: computeSelfAnalogStrength(matches),
          };
        }),
      );

      if (cancelled) return;

      const nextStrength: Record<string, SelfAnalogStrength> = {};
      const nextSnapshots: Record<string, StockAnalogSnapshot> = {};
      for (const r of results) {
        if (r.strength) nextStrength[r.symbol] = r.strength;
        if (r.snapshot) nextSnapshots[r.symbol] = r.snapshot;
      }
      setStrengthBySymbol(nextStrength);
      setSnapshotsBySymbol(nextSnapshots);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [symbolsKey, referenceDate]);

  const sortedRows = useMemo(
    () => sortReliablePicksByStrength(rows, strengthBySymbol),
    [rows, strengthBySymbol],
  );

  const topSymbol = sortedRows[0]?.symbol ?? rows[0]?.symbol ?? null;

  return {
    sortedRows,
    strengthBySymbol,
    snapshotsBySymbol,
    loading,
    topSymbol,
  };
}
